import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminShell from "../AdminShell";
import { adminFetch, BACKEND_URL } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-product-form.css";

/**
 * RAdminProductForm — /admin/products/new + /admin/products/:productId/edit
 *
 * Redesigned Product Form as a full routed page (design-ref/admin/Product
 * Form.html). Functionality replicated 1:1 from the legacy modal flow:
 * frontend/src/components/ProductForm.js opened by
 * frontend/src/pages/AdminProductsPage.js.
 *
 * Legacy behaviours carried over:
 *   - edit mode: GET /api/admin/products/{id} (full details) before editing
 *   - GET /api/categories to populate the category picker
 *   - full product model: name, slug, description, short_description, sku,
 *     price/regular_price/sale_price, stock_status, stock_quantity,
 *     categories[{id,name,slug}], images[{id,src,alt,position}], featured,
 *     weight, dimensions{length,width,height}, status, has_metal_options,
 *     metal_options[]
 *   - slug auto-generated from name while creating (or until a slug exists)
 *   - rich-text short/full description (bold / italic / bullet list /
 *     Normal-H1-H2-H3 block format -> HTML output via execCommand)
 *   - image upload: type + <=10MB validation, POST /api/admin/upload/image
 *     (multipart), src = BACKEND_URL + result.url; OR add image by URL;
 *     first image = Primary; per-image remove; placeholder on broken src
 *   - categories added by woocommerce_id with de-dupe, removable chips
 *   - metal options master checkbox clears the selection when unticked
 *   - validation: name required; price OR regular_price required
 *   - submit: woocommerce_id (edit: existing / create: Date.now()),
 *     created_at (edit: existing / create: now), updated_at now;
 *     PUT /api/admin/products/{id} or POST /api/admin/products
 *   - success -> toast + navigate back to /admin/products
 *     (legacy closed the modal and refreshed the list)
 *
 * Errors surface as toasts (redesign feedback system) instead of the legacy
 * inline red Alert banners.
 */

/* --------------------------------------------------------------------------
   Rich Text Editor — the legacy components/ProductForm.js editor re-skinned
   with the design's toolbar (B / I / • List / Normal ▾). Same execCommand
   capabilities, same HTML-string value contract.
   ------------------------------------------------------------------------ */
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  const formatText = (command, cmdValue = null) => {
    document.execCommand(command, false, cmdValue);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="pf-rte">
      <div className="pf-rte-toolbar">
        <button
          type="button"
          className="pf-rte-btn"
          onClick={() => formatText("bold")}
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          className="pf-rte-btn pf-rte-btn--i"
          onClick={() => formatText("italic")}
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          className="pf-rte-btn"
          onClick={() => formatText("insertUnorderedList")}
          title="Bullet List"
        >
          • List
        </button>
        <select
          className="pf-rte-btn pf-rte-select"
          onChange={(e) => formatText("formatBlock", e.target.value)}
          defaultValue=""
          title="Text style"
        >
          <option value="">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="pf-rte-editor"
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

const EMPTY_FORM = {
  name: "",
  slug: "",
  description: "",
  short_description: "",
  sku: "",
  price: "",
  regular_price: "",
  sale_price: "",
  stock_status: "instock",
  stock_quantity: "",
  categories: [],
  images: [],
  featured: false,
  weight: "",
  dimensions: { length: "", width: "", height: "" },
  status: "publish",
  has_metal_options: false,
  metal_options: [],
};

/* --------------------------------------------------------------------------
   Page body — lives inside AdminShell so useAdminToast() has its provider.
   ------------------------------------------------------------------------ */
const ProductFormBody = ({ productId, isEditing }) => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [sourceProduct, setSourceProduct] = useState(null); // full fetched product (edit mode)
  const [categories, setCategories] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(isEditing);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const navTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(navTimerRef.current), []);

  /* ---- GET /api/categories — same unauthenticated fetch as the legacy
         products page (errors only logged, never block the form) ---- */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/categories`
        );
        if (response.ok) {
          const categoriesData = await response.json();
          setCategories(categoriesData);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  /* ---- edit mode: GET /api/admin/products/{id} (full details), then
         populate every field exactly like the legacy ProductForm effect ---- */
  useEffect(() => {
    if (!isEditing) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingProduct(true);
        setLoadError("");
        const response = await adminFetch(`/api/admin/products/${productId}`, {
          navigate,
        });
        if (!response.ok) {
          throw new Error("Failed to fetch product details");
        }
        const product = await response.json();
        if (cancelled) return;
        setSourceProduct(product);
        setFormData({
          name: product.name || "",
          slug: product.slug || "",
          description: product.description || "",
          short_description: product.short_description || "",
          sku: product.sku || "",
          price: product.price || "",
          regular_price: product.regular_price || "",
          sale_price: product.sale_price || "",
          stock_status: product.stock_status || "instock",
          stock_quantity: product.stock_quantity || "",
          categories: product.categories || [],
          images: product.images || [],
          featured: product.featured || false,
          weight: product.weight || "",
          dimensions: product.dimensions || { length: "", width: "", height: "" },
          status: product.status || "publish",
          has_metal_options: product.has_metal_options || false,
          metal_options: product.metal_options || [],
        });
      } catch (err) {
        if (cancelled || (err && err.isAuthError)) return;
        setLoadError(err.message || "Failed to fetch product details");
        toast.error(err.message || "Failed to fetch product details");
      } finally {
        if (!cancelled) setLoadingProduct(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId, isEditing]);

  /* ---- field helpers (legacy ProductForm) ---- */
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDimensionChange = (dimension, value) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [dimension]: value,
      },
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim("-");
  };

  const handleNameChange = (name) => {
    handleInputChange("name", name);
    if (!isEditing || !formData.slug) {
      handleInputChange("slug", generateSlug(name));
    }
  };

  /* ---- images ---- */
  const addImage = () => {
    if (imageUrl.trim()) {
      const newImage = {
        id: Date.now().toString(),
        src: imageUrl.trim(),
        alt: formData.name || "Product image",
        position: formData.images.length,
      };
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, newImage],
      }));
      setImageUrl("");
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image file must be less than 10MB");
      return;
    }

    setUploadingImage(true);

    try {
      const fd = new FormData();
      fd.append("file", file);

      // adminFetch: Authorization only (multipart boundary set by browser);
      // missing/expired token redirects to /admin/login like the legacy check.
      const response = await adminFetch("/api/admin/upload/image", {
        method: "POST",
        body: fd,
        navigate,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to upload image");
      }

      const result = await response.json();

      // Add uploaded image to product images
      setFormData((prev) => {
        const newImage = {
          id: Date.now().toString(),
          src: `${BACKEND_URL}${result.url}`,
          alt: prev.name || "Product image",
          position: prev.images.length,
        };
        return {
          ...prev,
          images: [...prev.images, newImage],
        };
      });

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      if (err && err.isAuthError) return;
      toast.error(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (imageId) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img.id !== imageId),
    }));
  };

  /* ---- categories (value = woocommerce_id, de-duped on id) ---- */
  const addCategory = (categoryId) => {
    const category = categories.find(
      (cat) => cat.woocommerce_id.toString() === categoryId
    );
    if (
      category &&
      !formData.categories.find((cat) => cat.id === category.woocommerce_id)
    ) {
      const newCategory = {
        id: category.woocommerce_id,
        name: category.name,
        slug: category.slug,
      };
      setFormData((prev) => ({
        ...prev,
        categories: [...prev.categories, newCategory],
      }));
    }
  };

  const removeCategory = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.filter((cat) => cat.id !== categoryId),
    }));
  };

  /* ---- validation (legacy) ---- */
  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Product name is required");
      return false;
    }
    if (!formData.price && !formData.regular_price) {
      toast.error("Product price is required");
      return false;
    }
    return true;
  };

  /* ---- submit: PUT /api/admin/products/{id} or POST /api/admin/products ---- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const submitData = {
        ...formData,
        woocommerce_id: isEditing
          ? sourceProduct && sourceProduct.woocommerce_id
          : Date.now(), // Generate ID for new products (legacy behaviour)
        created_at: isEditing
          ? sourceProduct && sourceProduct.created_at
          : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const response = await adminFetch(
        isEditing ? `/api/admin/products/${productId}` : "/api/admin/products",
        {
          method: isEditing ? "PUT" : "POST",
          body: JSON.stringify(submitData),
          navigate,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? "update" : "create"} product`);
      }

      toast.success(
        isEditing
          ? "✓ Product updated successfully"
          : "✓ Product created successfully"
      );
      // brief pause so the toast is visible before returning to the list
      clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => navigate("/admin/products"), 900);
    } catch (err) {
      if (!(err && err.isAuthError)) {
        toast.error(err.message || "Error saving product");
      }
    } finally {
      setSaving(false);
    }
  };

  /* ---- loading / load-error states (edit mode) ---- */
  if (loadingProduct) {
    return (
      <div className="pg-admin-product-form">
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading product…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="pg-admin-product-form">
        <div className="adm-empty">
          <div className="adm-empty-icon">⚠️</div>
          <div className="adm-empty-title">Couldn’t load this product</div>
          <div className="adm-empty-sub">{loadError}</div>
          <Link to="/admin/products" className="adm-btn adm-btn--ghost pf-error-back">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pg-admin-product-form">
      <form className="pf-form" onSubmit={handleSubmit}>
        {/* ============ Basic Information ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Basic Information</div>

          <label className="adm-field">
            <span className="adm-label">Product Name *</span>
            <input
              className="adm-input"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Black Opal Pendant 9.75ct"
              required
            />
          </label>

          <label className="adm-field">
            <span className="adm-label">Slug</span>
            <input
              className="adm-input"
              value={formData.slug}
              onChange={(e) => handleInputChange("slug", e.target.value)}
              placeholder="black-opal-pendant-975ct"
            />
          </label>

          <div className="adm-label">Short Description</div>
          <div className="pf-rte-block">
            <RichTextEditor
              value={formData.short_description}
              onChange={(value) => handleInputChange("short_description", value)}
              placeholder="Brief product description"
            />
          </div>

          <div className="adm-label">Full Description</div>
          <RichTextEditor
            value={formData.description}
            onChange={(value) => handleInputChange("description", value)}
            placeholder="Detailed product description"
          />
        </section>

        {/* ============ Pricing & Inventory ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Pricing &amp; Inventory</div>

          <div className="pf-grid3">
            <label className="adm-field">
              <span className="adm-label">Regular Price *</span>
              <input
                className="adm-input"
                type="number"
                step="0.01"
                value={formData.regular_price}
                onChange={(e) => handleInputChange("regular_price", e.target.value)}
                placeholder="1450"
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">Sale Price</span>
              <input
                className="adm-input"
                type="number"
                step="0.01"
                value={formData.sale_price}
                onChange={(e) => handleInputChange("sale_price", e.target.value)}
                placeholder=""
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">SKU</span>
              <input
                className="adm-input"
                value={formData.sku}
                onChange={(e) => handleInputChange("sku", e.target.value)}
                placeholder="BOP-975"
              />
            </label>
          </div>

          <div className="pf-grid3 pf-grid3--end">
            <label className="adm-field">
              <span className="adm-label">Stock Status</span>
              <select
                className="adm-select"
                value={formData.stock_status}
                onChange={(e) => handleInputChange("stock_status", e.target.value)}
              >
                <option value="instock">In Stock</option>
                <option value="outofstock">Out of Stock</option>
                <option value="onbackorder">On Backorder</option>
              </select>
            </label>
            <label className="adm-field">
              <span className="adm-label">Stock Quantity</span>
              <input
                className="adm-input"
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => handleInputChange("stock_quantity", e.target.value)}
                placeholder="12"
              />
            </label>
            <label className="pf-inline-check pf-check-feat">
              <input
                type="checkbox"
                className="adm-check"
                checked={formData.featured}
                onChange={(e) => handleInputChange("featured", e.target.checked)}
              />{" "}
              Featured product
            </label>
          </div>
        </section>

        {/* ============ Metal Type Options ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Metal Type Options</div>

          <label className="pf-inline-check pf-metal-master">
            <input
              type="checkbox"
              className="adm-check"
              checked={formData.has_metal_options}
              onChange={(e) => {
                handleInputChange("has_metal_options", e.target.checked);
                if (!e.target.checked) {
                  handleInputChange("metal_options", []);
                }
              }}
            />{" "}
            This product has metal options
          </label>

          {formData.has_metal_options && (
            <div className="pf-metal-chips">
              <label className="pf-metal-chip">
                <input
                  type="checkbox"
                  className="adm-check"
                  checked={formData.metal_options.includes("sterling_silver")}
                  onChange={(e) => {
                    const newMetalOptions = e.target.checked
                      ? [...formData.metal_options, "sterling_silver"]
                      : formData.metal_options.filter(
                          (opt) => opt !== "sterling_silver"
                        );
                    handleInputChange("metal_options", newMetalOptions);
                  }}
                />{" "}
                Sterling Silver
              </label>
              <label className="pf-metal-chip">
                <input
                  type="checkbox"
                  className="adm-check"
                  checked={formData.metal_options.includes("gold_plated")}
                  onChange={(e) => {
                    const newMetalOptions = e.target.checked
                      ? [...formData.metal_options, "gold_plated"]
                      : formData.metal_options.filter(
                          (opt) => opt !== "gold_plated"
                        );
                    handleInputChange("metal_options", newMetalOptions);
                  }}
                />{" "}
                Sterling Silver Gold Plated
              </label>
            </div>
          )}
        </section>

        {/* ============ Categories ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Categories</div>

          {formData.categories.length > 0 && (
            <div className="pf-cat-chips">
              {formData.categories.map((category) => (
                <span key={category.id} className="pf-cat-chip">
                  {category.name}{" "}
                  <button
                    type="button"
                    className="pf-cat-x"
                    onClick={() => removeCategory(category.id)}
                    aria-label={`Remove ${category.name}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          <select
            className="adm-select pf-cat-select"
            value=""
            onChange={(e) => {
              if (e.target.value) addCategory(e.target.value);
            }}
          >
            <option value="">+ Add category…</option>
            {categories.map((category) => (
              <option
                key={category.woocommerce_id}
                value={category.woocommerce_id.toString()}
              >
                {category.name}
              </option>
            ))}
          </select>
        </section>

        {/* ============ Product Images ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Product Images</div>

          <div className="pf-img-grid">
            {formData.images.map((image, index) => (
              <div key={image.id} className="pf-img-tile">
                <img
                  src={image.src}
                  alt={image.alt}
                  onError={(e) => {
                    e.target.src = "https://placehold.co/100/100";
                  }}
                />
                {index === 0 && <span className="pf-img-primary">Primary</span>}
                <button
                  type="button"
                  className="pf-img-x"
                  onClick={() => removeImage(image.id)}
                  aria-label="Remove image"
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="pf-img-upload"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                "Uploading…"
              ) : (
                <>
                  + Upload
                  <br />
                  or URL
                </>
              )}
            </button>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageUpload}
            className="pf-file-hidden"
          />

          <div className="pf-url-row">
            <input
              className="adm-input"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Enter image URL"
            />
            <button type="button" className="adm-btn adm-btn--ghost" onClick={addImage}>
              + Add URL
            </button>
          </div>
          <div className="pf-img-hint">
            Supports JPG, PNG, GIF, WebP (max 10MB). The first image will be
            used as the primary product image.
          </div>
        </section>

        {/* ============ Physical Properties ============ */}
        <section className="adm-card">
          <div className="pf-sec-title">Physical Properties</div>

          <div className="pf-grid4">
            <label className="adm-field">
              <span className="adm-label">Weight (g)</span>
              <input
                className="adm-input"
                value={formData.weight}
                onChange={(e) => handleInputChange("weight", e.target.value)}
                placeholder="8"
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">Length (mm)</span>
              <input
                className="adm-input"
                value={formData.dimensions.length}
                onChange={(e) => handleDimensionChange("length", e.target.value)}
                placeholder="24"
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">Width (mm)</span>
              <input
                className="adm-input"
                value={formData.dimensions.width}
                onChange={(e) => handleDimensionChange("width", e.target.value)}
                placeholder="14"
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">Height (mm)</span>
              <input
                className="adm-input"
                value={formData.dimensions.height}
                onChange={(e) => handleDimensionChange("height", e.target.value)}
                placeholder="6"
              />
            </label>
          </div>
        </section>

        {/* ============ Actions ============ */}
        <div className="pf-actions">
          <Link to="/admin/products" className="adm-btn adm-btn--ghost">
            Cancel
          </Link>
          <button type="submit" className="adm-btn" disabled={saving}>
            {saving
              ? "Saving..."
              : isEditing
              ? "Update Product"
              : "Create Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

const RAdminProductForm = () => {
  const { productId } = useParams();
  const isEditing = !!productId;

  return (
    <AdminShell
      active="products"
      pageClass="pg-admin-product-form"
      eyebrow="Commerce"
      title={isEditing ? "Edit Product" : "Add New Product"}
      actions={
        <Link to="/admin/products" className="adm-btn adm-btn--ghost">
          ← Back to Products
        </Link>
      }
    >
      <ProductFormBody productId={productId} isEditing={isEditing} />
    </AdminShell>
  );
};

export default RAdminProductForm;
